import React from "react";
import MarketingNav from "../../components/MarketingNav";
import Footer from "../../components/Footer";
import LivePlayground from "../../components/LivePlayground";
import SEO from "../../components/SEO";
import { Link } from "react-router-dom";
import { Zap, Shield, Code2, GitBranch, ArrowRight, CheckCircle2 } from "lucide-react";

export default function SentryProduct() {
  return (
    <div>
      <SEO
        path="/sentry"
        title="Sentry · Inline AI Governance Proxy"
        description="Sentry is the inline governance proxy that stands between your app and every model provider. Enforce policies in SentryDSL, block prompt injection, and redact PII in under 4ms."
        keywords="inline AI proxy, LLM gateway, SentryDSL, prompt injection prevention, PII redaction LLM, policy enforcement AI"
      />
      <MarketingNav />
      <section className="container-x py-16">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-6">
            <span className="chip chip-orange mb-6"><Shield size={12}/> Sentry · In-line Governance</span>
            <h1 className="font-display text-4xl md:text-5xl font-bold text-navy leading-tight">
              The proxy that stands between your AI and every prompt.
            </h1>
            <p className="ink mt-5 text-lg max-w-xl">
              Sentry terminates model traffic, evaluates every request against your policies, and brokers it to the right provider — with a signed audit trail on every decision.
            </p>
            <div className="mt-8 grid grid-cols-2 gap-4">
              {[
                { k: "+6ms", v: "p95 hot-path overhead" },
                { k: "SentryDSL", v: "Rego-inspired policy language" },
                { k: "&lt;15s", v: "policy rollout across 1,000 pods" },
                { k: "100%", v: "signed, reproducible bundles" },
              ].map(x => (
                <div key={x.k} className="rounded-xl bg-white border border-[rgba(17,30,51,0.08)] p-4">
                  <div className="font-display text-2xl font-bold text-navy" dangerouslySetInnerHTML={{ __html: x.k }} />
                  <div className="text-xs ink mt-1">{x.v}</div>
                </div>
              ))}
            </div>
            <div className="mt-8 flex gap-3">
              <Link to="/register" className="btn-primary">Start trial <ArrowRight size={14}/></Link>
              <Link to="/pricing" className="btn-ghost">See tiers</Link>
            </div>
          </div>
          <div className="lg:col-span-6">
            <LivePlayground />
          </div>
        </div>
      </section>

      <section className="container-x py-16">
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { icon: Zap, title: "Gateway", desc: "Go-based in-line proxy. Native HTTP/2 + gRPC. Fails open or closed per tenant policy." },
            { icon: Code2, title: "SentryDSL", desc: "Author policies in a simple, Rego-inspired DSL. Compiled to bytecode. Content-addressable." },
            { icon: GitBranch, title: "Reproducibility", desc: "Every decision links to a policy bundle. Replay any decision at any time." },
          ].map(f => (
            <div key={f.title} className="rounded-2xl bg-white border border-[rgba(17,30,51,0.08)] p-7">
              <div className="w-10 h-10 rounded-lg bg-[#FF6B35]/12 flex items-center justify-center mb-4">
                <f.icon size={18} className="text-[#FF6B35]" />
              </div>
              <div className="font-display font-bold text-navy text-lg">{f.title}</div>
              <p className="ink text-sm mt-2">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="container-x pb-16">
        <div className="rounded-2xl bg-navy text-cream p-8">
          <div className="eyebrow text-[#FFD98A] mb-3">SentryDSL — example</div>
          <pre className="font-mono text-sm text-cream/90 overflow-x-auto">{`match: /\\b\\d{3}-\\d{2}-\\d{4}\\b/
action: deny
reason: "US SSN detected — HIPAA §164.514"

match: /[a-z0-9._%+-]+@[a-z0-9.-]+\\.[a-z]{2,}/i
action: redact
reason: "Email redacted — GDPR Art. 5"

match: /ignore (previous|all) instructions/i
action: deny
reason: "Prompt injection guard"`}</pre>
          <div className="mt-5 flex gap-2 flex-wrap">
            <span className="chip chip-cream"><CheckCircle2 size={12}/> Bytecode-compiled</span>
            <span className="chip chip-cream"><CheckCircle2 size={12}/> Content-addressable</span>
            <span className="chip chip-cream"><CheckCircle2 size={12}/> Signed with SHA-256</span>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
